import { Input as OldInput } from '@/components/inputs';
import { SimplePagination } from '@/components/tables/pagination';
import { useWorkspace } from '@/features/workspaces/hooks';
import { FormField } from '@/global/components';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/lib';
import { Audience } from '@/models/audiences';
import { DataSource, EDataSourceStatus } from '@/models/dataSource';
import { Trait } from '@/models/traits';
import { useLazyGetDataSourcesQuery } from '@/services/dataSources';
import { SearchIcon } from '@heroicons/react/outline';
import { Form, Formik, FormikProps } from 'formik';
import { some } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import * as Yup from 'yup';
import { DataSourceCheckbox } from './DataSourceCheckbox';

export type AudienceFormModel = Partial<Audience>;

type AudienceFormProps = {
  formRef: React.RefObject<FormikProps<AudienceFormModel>>;
  onSubmit: (form: AudienceFormModel) => void;
  audience?: Partial<Audience>;
  traits?: Trait[];
  readOnly?: boolean;
};

const radioOptionsDescription = {
  Email: 'audience_details.email_select',
  Phone: 'audience_details.phone_select',
  'User Id': 'audience_details.user_id_select',
  Username: 'audience_details.user_name_select',
  'Anonymous Id': 'audience_details.anonymous_select',
};

export const AudienceForm: FC<AudienceFormProps> = ({ audience, traits, onSubmit, formRef, readOnly }) => {
  const workspace = useWorkspace();
  const { t } = useTranslation('audience_edit');
  const limit = 5;

  const [getDataSources, { isLoading: isLoadingDataSources, data: dataSourcesData }] = useLazyGetDataSourcesQuery();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [lastPage, setLastPage] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const searchTextRef = useRef<string>('');

  useEffect(() => {
    let actualPageNo = pageNo;

    if (searchTextRef.current !== debouncedSearchText) {
      searchTextRef.current = debouncedSearchText;
      actualPageNo = 0;
      setPageNo(0);
    }

    getDataSources({
      workspaceId: workspace.id,
      offset: actualPageNo * limit,
      limit,
      status: EDataSourceStatus.ACTIVE,
      searchText: debouncedSearchText.length > 2 ? debouncedSearchText : undefined,
    });
  }, [debouncedSearchText, pageNo]);

  useEffect(() => {
    if (dataSourcesData && dataSourcesData.items && dataSourcesData.items.length) {
      setDataSources(dataSourcesData.items);
      setLastPage(dataSourcesData.items.length < limit);
    } else {
      setLastPage(true);
    }
  }, [dataSourcesData]);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('validations.required')),
    datasourceIds: Yup.array().required(t('validations.at_least_one_datasource_required')),
    matchingTrait: Yup.string().required(t('validations.required')),
  });

  return (
    <Formik
      innerRef={formRef}
      initialValues={{ ...audience }}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ setTouched, touched, values, errors, handleChange, setFieldError, setFieldValue }) => {
        return (
          <Form>
            <div className={'w-full text-left '}>
              <div className='flex flex-col gap-2 border-b py-8'>
                <h3 className={'font-bold'}>{t('step.audience.title')}</h3>
                <p className={'text-gray-600'}>{t('step.audience.description')}</p>
              </div>
            </div>
            <div className='mt-8 flex flex-col border-b border-secondary-border pb-16'>
              <FormField label={'audience_edit.step.audience.insert_name_label'}>
                <Input
                  name='name'
                  id='name'
                  type='text'
                  value={values.name || ''}
                  placeholder={t('audience_details.name_placeholder')}
                  onChange={(e) => {
                    setFieldError('name', undefined);
                    handleChange('name')(e.target.value);
                    setTouched({ name: true });
                  }}
                  errorMsg={errors.name}
                  className={'max-w-[450px] shadow'}
                  disabled={readOnly}
                />
              </FormField>
            </div>
            <div className='mt-8 flex flex-col'>
              {/* Unique identifier */}
              <div className='flex items-start justify-start gap-8 border-b border-secondary-border pb-10'>
                <div className='flex w-[280px] shrink-0 flex-col gap-2'>
                  <h3 className='text-lg font-semibold text-secondary-700'>{t('audience_details.matching_trait')}</h3>
                  <p className='text-sm text-tertiary-600'>{t('audience_details.matching_trait_description')}</p>
                </div>
                <div className='inline-flex max-w-[600px] grow flex-col'>
                  {errors.matchingTrait && <p className='text-left text-sm text-red-600'>{errors.matchingTrait}</p>}
                  {traits
                    ?.filter((trait) => trait.identifier)
                    .map((trait, index) => (
                      <div
                        key={trait.id}
                        onClick={() => {
                          if (readOnly) {
                            return;
                          }
                          setFieldError('matchingTrait', undefined);
                          setFieldValue('matchingTrait', trait.id);
                        }}
                        className={twMerge(
                          'inline-flex w-3/ p-2 rounded-sm items-center',
                          readOnly ? 'cursor-default' : 'cursor-pointer',
                          trait.id === values.matchingTrait ? 'border-brand-300 bg-brand-100' : 'border-gray-300',
                          index === 0 ? 'mt-0' : 'mt-2'
                        )}
                      >
                        <div className='mr-3 flex-none justify-start'>
                          <input
                            key={trait.id}
                            type='radio'
                            className={twMerge('form-radio', readOnly ? 'cursor-default' : 'cursor-pointer')}
                            name='radio'
                            value={trait.id}
                            checked={trait.id === values.matchingTrait}
                            disabled={readOnly}
                            onChange={() => {
                              setFieldError('matchingTrait', undefined);
                              setFieldValue('matchingTrait', trait.id);
                            }}
                          />
                        </div>
                        <div className=''>
                          <span className='font-medium text-gray-700'>{trait.displayName}</span>
                          <p className='text-sm text-tertiary-600'>{t(radioOptionsDescription[trait.displayName])}</p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Data sources */}
              <div className='flex items-start justify-start gap-8 border-b border-secondary-border py-10 '>
                <div className='flex w-[280px] shrink-0 flex-col gap-2'>
                  <div className='flex flex-col '>
                    <p className='text-left text-base font-medium text-gray-700'>{t('audience_details.datasources')}</p>
                    <p className='text-sm text-tertiary-600'>{t('audience_details.datasources_description')}</p>
                  </div>
                </div>
                <div className='flex flex-col'>
                  {/* Search */}
                  <div className='mb-4'>
                    <div className='w-72'>
                      <OldInput
                        icon={SearchIcon}
                        placeholder={t('audience_details.search_placeholder')}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        disabled={readOnly}
                      />
                    </div>
                    {errors.datasourceIds && <p className='mt-2 text-left text-sm text-red-600'>{errors.datasourceIds}</p>}
                  </div>
                  {dataSources.map((ds) => (
                    <DataSourceCheckbox
                      key={ds.id}
                      dataSource={ds}
                      traitId={values.matchingTrait}
                      checked={some(values.datasourceIds, (id) => id === ds.id)}
                      disabled={readOnly}
                      onChange={(e) => {
                        if (readOnly) {
                          return;
                        }
                        setFieldError('datasourceIds', undefined);
                        let newDataSourcesIds = values.datasourceIds || [];
                        if (e.target.checked) {
                          newDataSourcesIds = newDataSourcesIds.concat(ds.id);
                        } else {
                          newDataSourcesIds = newDataSourcesIds.filter((id) => id !== ds.id);
                        }
                        setFieldValue('datasourceIds', newDataSourcesIds);
                        if (!touched.name) {
                          const dataSourceNames = dataSources.filter((d) => newDataSourcesIds.includes(d.id)).map((d) => d.name);
                          setFieldValue('name', dataSourceNames.join(' and '));
                        }
                      }}
                    />
                  ))}
                  <div className='mt-2'>
                    <SimplePagination
                      firstPage={pageNo === 0}
                      lastPage={lastPage}
                      nextPage={() => setPageNo(pageNo + 1)}
                      prevPage={() => setPageNo(pageNo - 1)}
                      loading={isLoadingDataSources}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
